import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/lib/api';

/**
 * EditModal — Edit a single row (application or member) inline
 * fields: [{key, label, type?, options?}]
 */
const EditModal = ({ isOpen, onClose, entry, fields, endpoint, idField = 'id', onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      const init = {};
      fields.forEach(f => { init[f.key] = entry[f.key] ?? ''; });
      setForm(init);
    }
  }, [entry, fields]);

  if (!isOpen || !entry) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const id = entry[idField];
      // Convert empty strings to null for number fields
      const payload = { ...form };
      fields.forEach(f => {
        if (f.type === 'number' && (payload[f.key] === '' || payload[f.key] === null)) {
          payload[f.key] = null;
        } else if (f.type === 'number' && payload[f.key] !== null) {
          payload[f.key] = parseInt(payload[f.key], 10) || null;
        }
      });
      await apiClient.put(`${endpoint}/${id}`, payload);
      toast.success('Updated successfully');
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) or string errors
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to save');
      toast.error(errorMsg);
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        onClick={onClose} data-testid="edit-modal-overlay">
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white border-2 border-[#0F1B2D] w-full max-w-lg max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()} data-testid="edit-modal">

          {/* Header */}
          <div className="sticky top-0 bg-[#0F1B2D] px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h3 className="text-sm font-bold text-[#F5F3EE] uppercase tracking-wider">Edit Record</h3>
              <span className="text-[10px] font-mono text-[#C4A35A]">{entry[idField]}</span>
            </div>
            <button onClick={onClose} className="text-[#9B9589] hover:text-white transition-colors" data-testid="edit-modal-close"><X size={18} /></button>
          </div>

          {/* Fields */}
          <div className="p-6 space-y-4">
            {fields.map(f => (
              <div key={f.key}>
                <label className="block text-[9px] font-bold uppercase tracking-wider text-[#9B9589] mb-1.5">{f.label}</label>
                {f.type === 'select' && f.options ? (
                  <select value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="input-ainaa w-full h-10 px-3 text-sm" data-testid={`edit-${f.key}`}>
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="input-ainaa w-full px-3 py-2 text-sm min-h-[80px] resize-y" data-testid={`edit-${f.key}`} />
                ) : f.type === 'number' ? (
                  <Input type="number" value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="input-ainaa h-10" data-testid={`edit-${f.key}`} />
                ) : f.readOnly ? (
                  <div className="input-ainaa h-10 px-3 flex items-center text-sm text-[#9B9589] bg-[#F8F7F5]">{form[f.key]}</div>
                ) : (
                  <Input type="text" value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="input-ainaa h-10" data-testid={`edit-${f.key}`} />
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-white border-t border-[#D8D3CB] px-6 py-4 flex items-center justify-end gap-3">
            <button onClick={onClose} className="btn-ainaa-secondary px-5 py-2 text-xs font-bold uppercase tracking-wider" data-testid="edit-cancel">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="btn-ainaa-primary px-5 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2" data-testid="edit-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Changes
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EditModal;
